import { Component, ViewChild, AfterViewInit, ElementRef, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isNil } from 'lodash-es';
import numeral from 'numeral';
import { sys } from '../sys';

interface NumberBoxChangeTouchDelegate {
  (val: number): void;
}

@Component({
  selector: 'datex-common-numberbox',
  templateUrl: './numberbox.component.html',
  styleUrls: ['./numberbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberboxComponent),
      multi: true
    }
  ],
  standalone: false
})
export class NumberboxComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('input') input: ElementRef;

  @Input() format: string;
  @Input() placeholder = '';
  @Input() readOnly: boolean;

  disabled: boolean;
  formattedValue = null;
  _innerValue: number;

  ngAfterViewInit() {
    this.input.nativeElement.value = this.formattedValue;
  }

  // triggers when focus out and value has changed
  onValueChanged() {
    const parsedValue = this.parseValue(this.input.nativeElement.value);
    this.formattedValue = this.formatValue(parsedValue);
    this.value = parsedValue;

    // force the input value to be the formattedValue since formatting can yield something different
    this.input.nativeElement.value = this.formattedValue;
  }

  onFocusOut() {
    this.onValueChanged();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.onValueChanged();
    }
  }

  formatValue(value: number): string {
    let formattedValue;
    if (!isNil(value)) {
      formattedValue = this.format ?
        numeral(value).format(this.format) :
        value.toString();
    } else {
      formattedValue = value;
    }
    return formattedValue;
  }

  parseValue(value: string): number {
    let parsedValue = null;
    if (this.isDefinedTrimmed(value)) {
      parsedValue = this.format ?
        // the format is also a parse format
        numeral(numeral(value).format(this.format)).value() :
        numeral(value).value();
    }

    return parsedValue;
  }

  isDefinedTrimmed(value) {
    return sys.isDefinedTrimmed(value);
  }

  onChange: NumberBoxChangeTouchDelegate = () => void 0;
  onTouch: NumberBoxChangeTouchDelegate = () => void 0;

  get value(): number {
    return this._innerValue;
  }

  set value(val: number) {
    this._innerValue = val;
    this.onChange(val);
    this.onTouch(val);
  }

  writeValue(value: number): void {
    this.formattedValue = this.formatValue(value);
    this.input.nativeElement.value = this.formattedValue;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: NumberBoxChangeTouchDelegate): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: NumberBoxChangeTouchDelegate): void {
    this.onTouch = fn;
  }
}
